/**
 * PATH: packages/email-hub/src/feature/reader-header/triage/DecisionActions.tsx
 * FILE_ID: EMAILHUB-READERHEADER-DECISION-ACTIONS-001
 * PURPOSE: Render THE one confirm/reject cluster for the current decision:
 *   circles (or picker-primary affordance), More alternatives, the
 *   "+N reviews" pill, and the Review Queue overlay.
 * ROLE IN ARCHITECTURE: Adapter from the shared decision model to the Phase 2
 *   runtime + proven transports (plan D269-11/D269-12).
 * MAIN EXPORTS: ReaderDecisionActions.
 * NON-RESPONSIBILITIES: Queue ordering (buildDecisionQueue) and transports
 *   (useDecisionTransports) live in their own units.
 * NOTES FOR FUTURE AI: NEVER render legacy SmartActionBar / AiTriageBanner /
 *   AllocationChip confirm affordances here — the DOM architecture gate pins
 *   exactly one visible confirm/reject pair. Secondary decisions belong to
 *   the Review Queue only.
 */
import { useRef, useState } from 'react'
import { MoreHorizontal } from 'lucide-react'
import type { getSamKanbanApi } from '@quotech/kanban-host'
import { AdaptiveOverlay } from '@quotech/ui'

import { cn } from '../../../_internal/lib/utils'
import { EstimatorAllocator } from '../../components/EstimatorAllocator'
import { RehomeOrgControl } from '../../components/RehomeOrgControl'
import type { Email } from '../../hooks/useEmails'
import type { ReaderDecision } from '../model/types'
import type { ReaderActionRunner } from '../runtime/useReaderActionRunner'
import type { ReaderHeaderDensity } from '../responsive/density'
import { DecisionCircles } from './DecisionCircles'
import { decisionHue } from './hues'
import { ReaderReviewQueue } from './ReviewQueue'
import {
  decisionHasConfirmCircle,
  useDecisionTransports,
  type DecisionVerdict,
} from './useDecisionTransports'

export interface ReaderDecisionActionsProps {
  /** The single highest-priority pending decision (null hides the cluster). */
  decision: ReaderDecision | null
  /** The full live queue, priority order — drives the +N pill and the panel. */
  decisions: readonly ReaderDecision[]
  email: Email
  api: ReturnType<typeof getSamKanbanApi>
  density?: ReaderHeaderDensity
  /** Shared header runtime; accept/reject share one `decision:current` busyKey. */
  runner?: ReaderActionRunner
  onChanged?: () => void
  onPatchEmail?: (emailId: string, patch: Partial<Email>) => void
  /** Optimistic local queue advance + its rollback (adapter-owned state). */
  markResolved: (decisionId: string) => void
  unmarkResolved: (decisionId: string) => void
  onOpenCard?: (cardId: string) => void
  onOpenBoard?: () => void
  onOpenBill?: () => void
}

/** More-menu alternative rows per decision kind (pure I → O). */
function alternativesFor(
  decision: ReaderDecision,
  handlers: {
    onOpenCard?: (cardId: string) => void
    onOpenBoard?: () => void
    onOpenBill?: () => void
  },
): Array<{ id: string; label: string; run: () => void }> {
  const rows: Array<{ id: string; label: string; run: () => void }> = []
  if (decision.kind === 'placement' && decision.placement) {
    const placement = decision.placement
    if (handlers.onOpenCard) {
      rows.push({
        id: 'open-card',
        label: 'Open card',
        run: () => handlers.onOpenCard?.(placement.card_id),
      })
    }
    if (handlers.onOpenBoard) {
      rows.push({
        id: 'open-board',
        label: 'Open board',
        run: () => handlers.onOpenBoard?.(),
      })
    }
  }
  if (decision.kind === 'bill' && handlers.onOpenBill) {
    rows.push({
      id: 'view-bill',
      label: 'View the invoice',
      run: () => handlers.onOpenBill?.(),
    })
  }
  return rows
}

export function ReaderDecisionActions({
  decision,
  decisions,
  email,
  api,
  density = 'standard',
  runner,
  onChanged,
  onPatchEmail,
  markResolved,
  unmarkResolved,
  onOpenCard,
  onOpenBoard,
  onOpenBill,
}: ReaderDecisionActionsProps) {
  // Hook order is stable: transports mount regardless of the decision kind.
  const { resolve } = useDecisionTransports({
    email,
    api,
    runner,
    onPatchEmail,
    markResolved,
    unmarkResolved,
    onOpenBill,
  })
  const [lastSide, setLastSide] = useState<DecisionVerdict | null>(null)
  const [moreOpen, setMoreOpen] = useState(false)
  const [queueOpen, setQueueOpen] = useState(false)
  const moreRef = useRef<HTMLButtonElement>(null)
  const pillRef = useRef<HTMLButtonElement>(null)
  if (!decision) return null

  const hue = decisionHue(decision.kind)
  const phase = runner?.phaseFor('decision:current') ?? 'idle'
  const busy = phase !== 'idle'
  // Spinner only in the runner's deferred phases; sub-400ms stays instant.
  const busySide:
    | 'confirm'
    | 'reject'
    | null =
    phase === 'spinner' || phase === 'still-working'
      ? lastSide === 'accept'
        ? 'confirm'
        : lastSide
      : null

  // Reconcile rides the runner (its `reconcile` option); nothing extra here.
  const runResolve = (target: ReaderDecision, verdict: DecisionVerdict) => {
    setLastSide(verdict)
    resolve(target, verdict)
  }

  const alternatives = alternativesFor(decision, {
    onOpenCard,
    onOpenBoard,
    onOpenBill,
  })
  const hiddenReviews = decisions.length - 1

  return (
    <span className="flex items-center gap-1" data-testid="reader-decision-actions">
      {decision.kind === 'estimator' && (
        // Picker-primary affordance: choosing an estimator IS the accept.
        <EstimatorAllocator
          emailId={email.id}
          onAllocated={() => {
            markResolved(decision.id)
            onChanged?.()
          }}
        />
      )}
      {decision.kind === 'organisation' && (
        // Accept opens the explicit Move-organisation flow — never silent.
        <RehomeOrgControl emailId={email.id} onRehomed={onChanged} />
      )}
      <DecisionCircles
        hue={hue}
        confirmLabel={`Accept — ${decision.acceptEffect}`}
        rejectLabel={`Reject — ${decision.rejectEffect}`}
        onConfirm={
          decisionHasConfirmCircle(decision)
            ? () => runResolve(decision, 'accept')
            : undefined
        }
        onReject={() => runResolve(decision, 'reject')}
        busySide={busySide}
        disabled={busy}
      />
      {alternatives.length > 0 && (
        <>
          <button
            ref={moreRef}
            type="button"
            onClick={() => setMoreOpen((current) => !current)}
            className={cn(
              'relative inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-1',
              // change #269 C1: the decision band now grows on coarse
              // pointers to fit the real 44px confirm/reject circles
              // (DecisionCircles.tsx, DecisionLine.tsx) — this sibling gets
              // the SAME real growth instead of the WORK ITEM 7
              // pseudo-hit-area (a transparent overlay can't change what
              // getBoundingClientRect() reports).
              'pointer-coarse:h-11 pointer-coarse:w-11',
            )}
            aria-label="More decision options"
            aria-haspopup="menu"
            aria-expanded={moreOpen}
            data-testid="reader-decision-more"
          >
            <MoreHorizontal className="h-3.5 w-3.5" aria-hidden />
          </button>
          <AdaptiveOverlay
            open={moreOpen}
            onOpenChange={setMoreOpen}
            anchorRef={moreRef}
            mode={density === 'compact' ? 'sheet' : 'popover'}
            label="Decision alternatives"
            testId="reader-decision-more-menu"
            popoverWidth={240}
          >
            <div className="px-2 py-1 text-[10px] text-muted-foreground">
              {decision.explanation}
            </div>
            {alternatives.map((alternative) => (
              <button
                key={alternative.id}
                type="button"
                onClick={() => {
                  setMoreOpen(false)
                  alternative.run()
                }}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[11px] hover:bg-accent"
              >
                {alternative.label}
              </button>
            ))}
          </AdaptiveOverlay>
        </>
      )}
      {hiddenReviews > 0 && (
        <button
          ref={pillRef}
          type="button"
          onClick={() => setQueueOpen(true)}
          className={cn(
            'relative inline-flex h-6 items-center rounded-full border border-border bg-background px-2',
            'text-[10px] font-medium text-muted-foreground hover:bg-accent',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-1',
            // change #269 C1: real 44px height on coarse pointers (WORK
            // ITEM 7's pseudo-hit-area only faked the target — see
            // DecisionCircles.tsx). Width already clears 44px from the
            // "+N reviews" label; height alone was the gap.
            'pointer-coarse:h-11',
          )}
          aria-label={`Open the review queue (${decisions.length} pending)`}
          data-testid="reader-review-pill"
        >
          +{hiddenReviews} review{hiddenReviews === 1 ? '' : 's'}
        </button>
      )}
      <ReaderReviewQueue
        open={queueOpen}
        onOpenChange={setQueueOpen}
        decisions={decisions}
        onResolve={runResolve}
        compact={density === 'compact'}
        anchorRef={pillRef}
        onExhausted={() => {
          // Zero remaining → focus returns to the resolved summary line.
          document
            .querySelector<HTMLElement>(
              '[data-testid="reader-context-summary"] button',
            )
            ?.focus()
        }}
      />
    </span>
  )
}
